import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import type { BillingSubscription } from '../../types'

export interface CheckoutFeedback {
  kind: 'checkout' | 'switch'
  state: 'canceled' | 'awaiting_confirmation' | 'subscription_confirmed' | 'setup_confirmed' | 'switch_completed' | 'switch_recovery'
}

export function trialTerms(subscription: BillingSubscription) {
  if (subscription.status === 'trialing') return `Trial in progress${subscription.trial_ends_display ? ` until ${subscription.trial_ends_display}` : ''}. Billing begins after the trial.`
  if (subscription.trial_requested && !subscription.trial_consumed && !subscription.subscription_confirmed) {
    return `Your selected ${subscription.trial_days ? `${subscription.trial_days}-day ` : ''}trial starts only after card setup. No trial end date is scheduled yet.`
  }
  if (subscription.subscription_confirmed) return 'Your subscription is set up. See the renewal date for the next charge.'
  return `${subscription.trial_consumed ? 'The trial has already been used. ' : ''}${subscription.plan.price_display || 'The selected plan'} is billed immediately after payment setup.`
}

export function switchTerms(subscription: BillingSubscription) {
  const change = subscription.pending_payer_switch
  if (!change) return ''
  const actor = change.to === 'company' ? 'The company Admin' : 'The responsible Guide'
  if (['payment_recovery_pending', 'payment_failed', 'fallback_pending'].includes(change.execution_stage || '')) return `${actor} must resolve payment recovery. The payer change is not complete.`
  if (change.setup_completed) return `Payment method confirmed. ${change.execution_stage === 'reservation_pending' ? 'Stripe reservation is pending. ' : ''}The payer change is not complete; it takes effect at ${change.effective_at_display || 'the coverage boundary'}.`
  return `${actor} must finish secure payment setup. Current coverage remains in place until ${change.effective_at_display || 'the coverage boundary'}; setup alone does not complete the payer change.`
}

export function BillingReturnNotice({ feedback }: { feedback?: CheckoutFeedback | null }) {
  const [attempts, setAttempts] = useState(0)
  const waiting = feedback?.state === 'awaiting_confirmation'
  useEffect(() => {
    if (!waiting || attempts >= 6) return
    const timer = window.setTimeout(() => {
      setAttempts(value => value + 1)
      router.reload({ only: ['subscription', 'presentation', 'checkout_feedback', 'billing_read_only'] })
    }, 3000)
    return () => window.clearTimeout(timer)
  }, [waiting, attempts])
  if (!feedback) return null
  const messages = {
    canceled: 'Checkout was canceled. No completion is confirmed. Resume secure setup below when ready.',
    awaiting_confirmation: attempts < 6
      ? 'Returned from Stripe; awaiting confirmation. We are checking for updates. Please do not start another checkout.'
      : 'Stripe confirmation is taking longer than expected. Check again, or resume the existing secure setup below if it was not completed. If you already submitted payment, contact support before trying again.',
    subscription_confirmed: 'Subscription setup is confirmed by Stripe.',
    setup_confirmed: 'Payment method setup is confirmed. The payer change is still pending the coverage boundary; the current payer has not changed.',
    switch_completed: 'The payer change is confirmed.',
    switch_recovery: 'The payer change needs recovery and is not complete. Review the responsible payer’s next action below or contact support.',
  }
  return <div role="status" className="rounded-xl border border-stone/30 bg-white p-4 text-sm text-text-primary">
    {messages[feedback.state]}
    {waiting && attempts >= 6 && <button className="ml-3 font-semibold underline" onClick={() => { setAttempts(0); router.reload({ only: ['subscription', 'presentation', 'checkout_feedback', 'billing_read_only'] }) }}>Check again</button>}
    {waiting && attempts >= 6 && <button className="ml-3 font-semibold underline" onClick={() => router.visit(window.location.pathname)}>Return to setup options</button>}
  </div>
}